import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { useChannels, useRoles } from "@/lib/feature-hooks";
import { toast } from "sonner";
import { Plus, Trash2, Heart } from "lucide-react";
import EmptyServer from "@/components/dashboard/EmptyServer";

export default function ReactionRolesPanel() {
  const { active } = useServer();
  const channels = useChannels(active?.id, active?.bot_present);
  const roles = useRoles(active?.id, active?.bot_present);
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ channel_id: "", message: "Pick your roles!", pairs: [{ emoji: "🎮", role_id: "" }] });

  const refresh = () => {
    if (!active?.id || !active.bot_present) return;
    api.get(`/features/reaction-roles/${active.id}`).then((r) => setList(r.data));
  };
  useEffect(refresh, [active?.id, active?.bot_present]);

  if (!active) return <EmptyServer />;
  if (!active.bot_present) return <EmptyServer needsBot />;

  const create = async () => {
    if (!form.channel_id || form.pairs.some((p) => !p.emoji || !p.role_id)) {
      toast.error("Fill channel + emoji/role for each pair");
      return;
    }
    try {
      const pairs = form.pairs.map((p) => ({ ...p, role_name: roles.find((r) => r.id === p.role_id)?.name || "" }));
      await api.post(`/features/reaction-roles/${active.id}`, { ...form, pairs });
      toast.success("Reaction roles message posted!");
      setForm({ channel_id: "", message: "Pick your roles!", pairs: [{ emoji: "🎮", role_id: "" }] });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/features/reaction-roles/${active.id}/${id}`);
    refresh();
  };

  return (
    <div data-testid="reaction-roles-panel" className="space-y-8">
      <div>
        <div className="overline mb-2"><Heart className="inline w-4 h-4 mr-2 -mt-1" />ESSENTIALS</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Reaction Roles</h1>
        <p className="text-zinc-400 mt-2">Members tap an emoji → bot grants a role. Self-service onboarding.</p>
      </div>

      <div className="tactical-card p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="overline block mb-2">Channel</label>
            <select className="tactical-input" value={form.channel_id} onChange={(e) => setForm({ ...form, channel_id: e.target.value })} data-testid="rr-channel">
              <option value="">— Select —</option>
              {channels.filter((c) => c.type === "text").map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-2">Header message</label>
            <input className="tactical-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="rr-message" />
          </div>
        </div>

        <div className="space-y-2">
          <div className="overline">Emoji → Role pairs</div>
          {form.pairs.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className="tactical-input w-24 text-center" value={p.emoji} onChange={(e) => {
                const pp = [...form.pairs]; pp[i].emoji = e.target.value; setForm({ ...form, pairs: pp });
              }} data-testid={`rr-emoji-${i}`} />
              <select className="tactical-input flex-1" value={p.role_id} onChange={(e) => {
                const pp = [...form.pairs]; pp[i].role_id = e.target.value; setForm({ ...form, pairs: pp });
              }} data-testid={`rr-role-${i}`}>
                <option value="">— Role —</option>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <button onClick={() => setForm({ ...form, pairs: form.pairs.filter((_, j) => j !== i) })} className="btn-ghost border border-brand-line">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button onClick={() => setForm({ ...form, pairs: [...form.pairs, { emoji: "🎯", role_id: "" }] })} className="btn-secondary text-sm">
            <Plus className="w-4 h-4" /> Add pair
          </button>
        </div>

        <div className="flex justify-end pt-2">
          <button onClick={create} className="btn-primary" data-testid="rr-create-btn"><Plus className="w-4 h-4" /> Post message</button>
        </div>
      </div>

      <div className="tactical-card p-6">
        <h2 className="font-heading text-xl font-bold uppercase mb-4">Active ({list.length})</h2>
        <div className="space-y-2">
          {list.length === 0 && <div className="text-zinc-500 font-mono-tabular text-sm">No reaction-role messages yet</div>}
          {list.map((r) => (
            <div key={r.id} className="border border-brand-line p-3 rounded-lg flex items-start gap-4" data-testid={`rr-row-${r.id}`}>
              <div className="flex-1">
                <div className="text-sm font-semibold">Channel: <code className="text-brand-orange">{r.channel_id}</code></div>
                <div className="text-xs text-zinc-500 mt-1">
                  {r.pairs.map((p) => `${p.emoji} → ${p.role_name}`).join("  ·  ")}
                </div>
              </div>
              <button onClick={() => remove(r.id)} className="text-red-400 hover:text-red-300 p-2"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
